import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import gsap from 'gsap'
import {
  api, ApiError, hintText, purposeLabel, statusLabel,
  type DnsRecord, type Domain, type Me, type VerifyResult,
} from '../lib/api'
import { useGsap } from '../lib/useGsap'

// ==================================================================== shared

function StatusBadge({ status }: { status: Domain['status'] }) {
  const s = statusLabel[status]
  // Glyph AND word, never colour alone.
  return <span className={`badge ${s.cls}`}>{s.glyph} {s.text}</span>
}

function Copyable({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(value)
    } catch {
      // Clipboard API needs a secure context and can still be denied. Fall back to a
      // selection so the value is never un-copyable.
      const ta = document.createElement('textarea')
      ta.value = value
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      ta.remove()
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 1400)
  }

  return (
    <div className="copyable">
      <span style={{ flex: 1 }}>{value}</span>
      <button className="secondary" onClick={copy} aria-label="Copy to clipboard">
        {copied ? '✓ Copied' : 'Copy'}
      </button>
    </div>
  )
}

type HostMode = 'label' | 'full'

/** The label form of a host: the part before ".{domain}", or "@" for an apex record. */
function toHostLabel(fullHost: string, domainName: string): string {
  if (fullHost === domainName) return '@'
  if (domainName && fullHost.endsWith(`.${domainName}`)) {
    return fullHost.slice(0, -(domainName.length + 1))
  }
  return fullHost
}

/**
 * One toggle for the whole page: label vs full host name.
 *
 * Registrars disagree — Porkbun and cPanel append the domain (so you type just the label),
 * while Cloudflare and Route 53 want the full name. Rather than repeat a switch on every
 * record, the customer sets it once here and every Host below follows.
 */
function HostModeToggle({ mode, onChange }: { mode: HostMode; onChange: (m: HostMode) => void }) {
  return (
    <div className="host-mode">
      <span className="small muted">Host format for your registrar:</span>
      <div className="host-toggle" role="tablist" aria-label="Host format">
        <button role="tab" aria-selected={mode === 'label'}
                className={mode === 'label' ? 'active' : ''} onClick={() => onChange('label')}>
          Label only
        </button>
        <button role="tab" aria-selected={mode === 'full'}
                className={mode === 'full' ? 'active' : ''} onClick={() => onChange('full')}>
          Full name
        </button>
      </div>
      <span className="small muted">
        {mode === 'label' ? 'Porkbun, cPanel, most others' : 'Cloudflare, Route 53'}
      </span>
    </div>
  )
}

// ==================================================================== list

export function Domains({ me }: { me: Me }) {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [inbound, setInbound] = useState(false)
  const [error, setError] = useState<ApiError | null>(null)
  // List filters + pagination.
  const [q, setQ] = useState('')
  const [statusF, setStatusF] = useState<'all' | 'ready' | 'pending'>('all')
  const [dpage, setDpage] = useState(0)
  const DPAGE = 10

  const { data, isLoading } = useQuery({ queryKey: ['domains'], queryFn: api.domains.list })

  const create = useMutation({
    mutationFn: () => api.domains.create({ name: name.trim().toLowerCase(), inbound }),
    onSuccess: async (d) => {
      setName('')
      setError(null)
      await qc.invalidateQueries({ queryKey: ['domains'] })
      // Straight to the DNS records. The whole point of adding a domain is to publish
      // them, so making the customer find that screen is pure friction.
      navigate(`/domains/${d.id}`)
    },
    onError: (e) => setError(e as ApiError),
  })

  const remove = useMutation({
    mutationFn: (id: string) => api.domains.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['domains'] }),
  })

  const ref = useGsap(({ el }) => {
    gsap.from(el.querySelectorAll('.dom-row'), {
      y: 10, opacity: 0, duration: 0.3, stagger: 0.04, ease: 'power2.out',
    })
  }, [data?.data.length])

  const atLimit = false // Domains are unlimited on every plan.

  // Client-side filter + paginate (the list is plan-bounded, so no server round-trip needed).
  const allDomains = data?.data ?? []
  const filtered = allDomains.filter(d =>
    (statusF === 'all' || (statusF === 'ready' ? d.can_send : !d.can_send)) &&
    (!q.trim() || d.name.toLowerCase().includes(q.trim().toLowerCase())))
  const dtotal = filtered.length
  const dpaged = filtered.slice(dpage * DPAGE, dpage * DPAGE + DPAGE)

  return (
    <div>
      <h1>Domains</h1>
      <p className="lede">
        Add a domain, publish the records we generate, then verify. Every record points at
        a hostname we control, so an IP change never requires you to touch DNS again.
      </p>

      <div className="card" style={{ marginBottom: 24 }}>
        <form
          onSubmit={e => { e.preventDefault(); if (name.trim()) create.mutate() }}
          className="row"
          style={{ flexWrap: 'wrap', alignItems: 'flex-end' }}
        >
          <div className="field" style={{ flex: '1 1 260px', marginBottom: 0 }}>
            <label htmlFor="dom">Domain</label>
            <input id="dom" placeholder="example.com" value={name}
                   onChange={e => setName(e.target.value)} disabled={atLimit} />
          </div>
          <label className="row small" style={{ fontWeight: 400, marginBottom: 8 }}>
            <input type="checkbox" style={{ width: 16, height: 16 }}
                   checked={inbound} onChange={e => setInbound(e.target.checked)} />
            <span>Also receive mail (adds MX records)</span>
          </label>
          <button type="submit" disabled={!name.trim() || create.isPending || atLimit}>
            {create.isPending ? 'Adding…' : 'Add domain'}
          </button>
        </form>

        {atLimit && (
          <div className="hint" style={{ marginTop: 10 }}>
            Your {me.plan?.name} plan allows {me.plan?.max_domains} domain(s).
          </div>
        )}

        {error && (
          <div className="alert error" style={{ marginTop: 12, marginBottom: 0 }}>
            {error.message}
          </div>
        )}
      </div>

      {isLoading && <div className="skeleton" style={{ height: 120 }} />}

      {!isLoading && allDomains.length === 0 && (
        <div className="empty">
          <div className="big">◇</div>
          <p><strong>No domains yet</strong></p>
          <p>Add the domain you want to send from. It takes about five minutes.</p>
        </div>
      )}

      {allDomains.length > 0 && (
        <div className="toolbar" style={{ marginBottom: 12 }}>
          <input placeholder="Search domains…" value={q}
                 onChange={e => { setQ(e.target.value); setDpage(0) }} style={{ flex: '1 1 240px' }} />
          <div className="segmented" role="tablist" aria-label="Filter domains">
            {(['all', 'ready', 'pending'] as const).map(f => (
              <button key={f} className={statusF === f ? 'active' : ''}
                      onClick={() => { setStatusF(f); setDpage(0) }}>
                {f === 'all' ? 'All' : f === 'ready' ? 'Ready to send' : 'Needs setup'}
              </button>
            ))}
          </div>
          <span className="small muted">{dtotal} of {allDomains.length}</span>
        </div>
      )}

      {allDomains.length > 0 && dtotal === 0 && (
        <div className="empty" style={{ padding: 28 }}><p className="muted">No domains match.</p></div>
      )}

      {dpaged.length > 0 && (
        <div ref={ref} className="card flush">
          {dpaged.map(d => (
            <Link key={d.id} to={`/domains/${d.id}`} className="dom-row list-row">
              <div>
                <div className="name">{d.name}</div>
                <div className="small muted">
                  {d.can_send ? 'Ready to send' : 'Publish the DNS records to start sending'}
                  {d.inbound_enabled && ' · inbound enabled'}
                </div>
              </div>
              <div className="row" style={{ gap: 10, alignItems: 'center' }}>
                <StatusBadge status={d.status} />
                <button className="ghost small danger"
                        onClick={e => {
                          e.preventDefault(); e.stopPropagation()
                          if (confirm(`Remove ${d.name}? It is soft-deleted — history is kept, and you can re-add the domain later.`)) {
                            remove.mutate(d.id)
                          }
                        }}>
                  Remove
                </button>
              </div>
            </Link>
          ))}
        </div>
      )}

      {dtotal > DPAGE && (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 12 }}>
          <span className="small muted">
            Showing {dpage * DPAGE + 1}–{Math.min(dtotal, dpage * DPAGE + dpaged.length)} of {dtotal}
          </span>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn-ghost" disabled={dpage === 0} onClick={() => setDpage(p => Math.max(0, p - 1))}>← Prev</button>
            <span className="small muted">Page {dpage + 1} of {Math.ceil(dtotal / DPAGE)}</span>
            <button className="btn-ghost" disabled={(dpage + 1) * DPAGE >= dtotal} onClick={() => setDpage(p => p + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ==================================================================== detail

export function DomainDetail() {
  const { id = '' } = useParams()
  const qc = useQueryClient()
  const [result, setResult] = useState<VerifyResult | null>(null)

  const { data: domain } = useQuery({
    queryKey: ['domain', id],
    queryFn: () => api.domains.get(id),
    enabled: !!id,
  })

  const { data: dns, isLoading } = useQuery({
    queryKey: ['dns', id],
    queryFn: () => api.domains.dns(id),
    enabled: !!id,
  })

  const verify = useMutation({
    mutationFn: () => api.domains.verify(id),
    onSuccess: async (r) => {
      setResult(r)
      await qc.invalidateQueries({ queryKey: ['dns', id] })
      await qc.invalidateQueries({ queryKey: ['domain', id] })
      await qc.invalidateQueries({ queryKey: ['domains'] })

      if (r.can_send) {
        // The emotional high point of onboarding. Worth the animation budget —
        // this is the moment the product starts working for them (docs/08).
        gsap.fromTo('.verify-success',
          { scale: 0.94, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.45, ease: 'back.out(1.6)' })
      }
    },
  })

  const rotate = useMutation({
    mutationFn: () => api.domains.rotateDkim(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['dns', id] })
      await qc.invalidateQueries({ queryKey: ['domain', id] })
    },
  })

  const setInbound = useMutation({
    mutationFn: (enabled: boolean) => api.domains.update(id, { inbound: enabled }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['dns', id] })
      await qc.invalidateQueries({ queryKey: ['domain', id] })
      await qc.invalidateQueries({ queryKey: ['domains'] })
    },
  })

  const navigate = useNavigate()
  const remove = useMutation({
    mutationFn: () => api.domains.remove(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['domains'] })
      navigate('/domains')
    },
  })

  const status = result?.status ?? domain?.status
  const canSend = result?.can_send ?? domain?.can_send

  const records: DnsRecord[] = result?.records
    ?? [...(dns?.required ?? []), ...(dns?.recommended ?? [])]


  const [hostMode, setHostMode] = useState<HostMode>('label')
  const [tab, setTab] = useState<'outbound' | 'inbound' | 'optional'>('outbound')
  const domainName = domain?.name ?? dns?.domain ?? ''

  // Outbound now carries everything that affects whether your mail is trusted and reaches the
  // inbox — the two that let you send at all (ownership + DKIM) plus the three that keep you
  // out of spam (SPF, return-path alignment, DMARC). Tracking is the only truly-optional one.
  const outboundRequired = records.filter(r => r.required && (r.purpose === 'ownership' || r.purpose === 'dkim'))
  const deliverability = records.filter(r => r.purpose === 'spf' || r.purpose === 'return_path' || r.purpose === 'dmarc')
  const inbound = records.filter(r => r.purpose === 'mx')
  const optional = records.filter(r => r.purpose === 'tracking')

  const groupOk = (rs: DnsRecord[]) => rs.length > 0 && rs.every(r => r.status === 'ok')
  const deliverabilityOk = deliverability.length === 0 || deliverability.every(r => r.status === 'ok')

  return (
    <div>
      <p className="small"><Link to="/domains">← Domains</Link></p>

      <div className="spread" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ marginBottom: 6 }}>{domain?.name ?? dns?.domain ?? '…'}</h1>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            {status && <StatusBadge status={status} />}
            {domain && (
              <span className={`badge ${domain.inbound_enabled ? 'ok' : ''}`}>
                {domain.inbound_enabled ? 'Inbound on' : 'Sending only'}
              </span>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button onClick={() => verify.mutate()} disabled={verify.isPending}>
            {verify.isPending ? 'Checking DNS…' : 'Verify'}
          </button>
          <button className="ghost danger"
                  disabled={remove.isPending}
                  onClick={() => {
                    if (confirm(
                      `Remove ${domain?.name ?? 'this domain'}?\n\nThis is a soft delete — delivery history is kept and the name can be re-added later, but the domain stops sending and disappears from your list.`)) {
                      remove.mutate()
                    }
                  }}>
            {remove.isPending ? 'Removing…' : 'Remove'}
          </button>
        </div>
      </div>

      {canSend ? (
        <div className="verify-success alert info" style={{ marginTop: 18 }}>
          <div>
            <strong>✓ Verified — you can send now.</strong>
            {!deliverabilityOk && (
              <p className="small" style={{ margin: '4px 0 0' }}>
                Sending works, but the deliverability records (SPF, return-path, DMARC) in the
                <b> Outbound</b> tab aren’t all set — until they are, mail can land in spam.
                Publish them to reach the inbox reliably.
              </p>
            )}
          </div>
        </div>
      ) : status === 'drifted' ? (
        <div className="alert error" style={{ marginTop: 18 }}>
          <strong>A record disappeared.</strong> Re-publish the failing record below; sending
          pauses after a short grace period.
        </div>
      ) : (
        <div className="alert warn" style={{ marginTop: 18 }}>
          Add the <b>Outbound</b> records below, then press Verify.
        </div>
      )}

      <div className="small muted" style={{ margin: '12px 0 4px' }}>
        In your registrar’s <b>Host</b> field paste the value from the toggle below. On
        Cloudflare, keep CNAMEs <b>DNS only</b> (grey cloud).
        {domain?.last_checked_at && ` · checked ${new Date(domain.last_checked_at).toLocaleTimeString()}`}
      </div>

      {isLoading && <div className="skeleton" style={{ height: 200 }} />}

      <HostModeToggle mode={hostMode} onChange={setHostMode} />

      <div className="tabs" role="tablist" style={{ marginTop: 6 }}>
        <button role="tab" aria-selected={tab === 'outbound'}
                className={tab === 'outbound' ? 'active' : ''} onClick={() => setTab('outbound')}>
          Outbound
          <span className={`tab-dot ${groupOk(outboundRequired) && deliverabilityOk ? 'ok' : 'bad'}`} />
        </button>
        <button role="tab" aria-selected={tab === 'inbound'}
                className={tab === 'inbound' ? 'active' : ''} onClick={() => setTab('inbound')}>
          Inbound
          {inbound.length > 0 && <span className={`tab-dot ${groupOk(inbound) ? 'ok' : 'bad'}`} />}
        </button>
        <button role="tab" aria-selected={tab === 'optional'}
                className={tab === 'optional' ? 'active' : ''} onClick={() => setTab('optional')}>
          Optional
          <span className="tab-count">{optional.filter(r => r.status === 'ok').length}/{optional.length}</span>
        </button>
      </div>

      {tab === 'outbound' && (
        <>
          <p className="small muted" style={{ margin: '4px 0 12px' }}>Required to send email.</p>
          {outboundRequired.map((r, i) => <RecordRow key={`o-${i}`} record={r} domainName={domainName} hostMode={hostMode} />)}

          {deliverability.length > 0 && (
            <>
              <div className="deliver-divider">
                <strong>Recommended — reach the inbox, not spam</strong>
                <span>Sending works without these, but publish them so Gmail and Yahoo trust
                  your mail: SPF authorises our servers, the return-path aligns SPF to your
                  domain, and DMARC ties it together.</span>
              </div>
              {deliverability.map((r, i) => <RecordRow key={`d-${i}`} record={r} domainName={domainName} hostMode={hostMode} />)}
            </>
          )}
        </>
      )}

      {tab === 'inbound' && (
        domain?.inbound_enabled ? (
          <>
            <div className="spread" style={{ margin: '4px 0 12px', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <p className="small muted" style={{ margin: 0 }}>Required to receive email on this domain. Add the MX records below.</p>
              <button className="ghost small danger"
                      disabled={setInbound.isPending}
                      onClick={() => {
                        if (confirm('Turn off inbound receiving?\n\nThe MX records will be removed from the checklist and this domain will stop accepting mail. You can re-enable it any time.')) {
                          setInbound.mutate(false)
                        }
                      }}>
                {setInbound.isPending ? 'Saving…' : 'Disable inbound'}
              </button>
            </div>
            {inbound.map((r, i) => <RecordRow key={`i-${i}`} record={r} domainName={domainName} hostMode={hostMode} />)}
          </>
        ) : (
          <div className="empty">
            <p><strong>Inbound not enabled</strong></p>
            <p>This domain sends only. Enable inbound to receive mail — we’ll generate the MX
              records for you to publish, and route incoming mail to your inbound stream.</p>
            <button style={{ marginTop: 12 }}
                    disabled={setInbound.isPending}
                    onClick={() => setInbound.mutate(true)}>
              {setInbound.isPending ? 'Enabling…' : 'Enable inbound receiving'}
            </button>
          </div>
        )
      )}

      {tab === 'optional' && (
        optional.length > 0 ? (
          <>
            <p className="small muted" style={{ margin: '4px 0 12px' }}>
              Open and click tracking on your own domain. Purely optional — it does not affect
              deliverability, only whether tracked links use your domain instead of ours.
            </p>
            {optional.map((r, i) => <RecordRow key={`x-${i}`} record={r} domainName={domainName} hostMode={hostMode} />)}
          </>
        ) : (
          <div className="empty"><p><strong>Nothing optional left</strong></p><p>All deliverability records moved to the Outbound tab.</p></div>
        )
      )}

      {domain && (
        <>
          <h2>DKIM key</h2>
          <div className="card">
            <p className="small muted" style={{ marginTop: 0 }}>
              Your mail is signed with a private key we hold; the matching public key is the
              <b> s1._domainkey</b> TXT record in the <b>Outbound</b> tab, which you publish on
              your own domain. Rotating provisions a fresh key that you then re-publish.
            </p>

            <div className="row" style={{ marginTop: 4, gap: 10, alignItems: 'center' }}>
              <button className="secondary"
                      disabled={rotate.isPending}
                      onClick={() => {
                        if (confirm(
                          'Rotate the DKIM key?\n\nOnly do this for periodic key hygiene, not '
                          + 'during setup. It switches signing to your standby selector — mail '
                          + 'keeps working, but the new key must propagate. Do not rotate '
                          + 'repeatedly.')) {
                          rotate.mutate()
                        }
                      }}>
                {rotate.isPending ? 'Rotating…' : 'Rotate DKIM key'}
              </button>
              {rotate.isError && (
                <span className="small crit">{(rotate.error as ApiError)?.message ?? 'Could not rotate.'}</span>
              )}
              {rotate.isSuccess && <span className="small" style={{ color: 'var(--success)' }}>Rotated.</span>}
            </div>
            <div className="hint">
              Switches signing to your standby selector and provisions a fresh one. The
              retired key stays published, because mail already signed with it is still in
              flight for days. A key under an hour old cannot be rotated.
            </div>
          </div>
        </>
      )}
    </div>
  )
}

/** Merge an existing SPF (and any duplicates) with ours into one valid record. A domain may
 *  have only one v=spf1 TXT, so this is the fix we hand the user directly. */
function mergeSpf(observed: string, ours: string): string | null {
  const records = observed.split('|').map(s => s.trim()).filter(s => /^v=spf1/i.test(s))
  if (records.length === 0) return null
  const mechs: string[] = []
  let allQ = '~all'
  const push = (rec: string) => {
    for (const p of rec.split(/\s+/).slice(1)) {
      if (/^[+\-~?]?all$/i.test(p)) { allQ = p; continue }
      if (!mechs.includes(p)) mechs.push(p)
    }
  }
  records.forEach(push)
  const ourInclude = ours.match(/include:\S+/i)?.[0]
  if (ourInclude && !mechs.includes(ourInclude)) mechs.push(ourInclude)
  return `v=spf1 ${mechs.join(' ')} ${allQ}`.replace(/\s+/g, ' ').trim()
}

function RecordRow({ record, domainName, hostMode }: { record: DnsRecord; domainName: string; hostMode: HostMode }) {
  const cls = record.status === 'ok' ? 'ok'
    : record.status === 'pending' ? 'pending' : 'failed'
  // Problems open by default so the user sees what to fix; verified records collapse away.
  const [open, setOpen] = useState(record.status !== 'ok')

  const glyph = record.status === 'ok' ? '✓'
    : record.status === 'pending' ? '○' : '!'

  const label = record.status === 'ok' ? 'Found'
    : record.status === 'pending' ? 'Not checked yet'
      : record.status === 'missing' ? 'Not found' : 'Does not match'

  // SPF conflict helper: existing SPF present and it isn't already exactly ours.
  const spfMerged = record.purpose === 'spf' && record.status !== 'ok' && record.observed
    ? mergeSpf(record.observed, record.value) : null
  const hasExistingSpf = spfMerged && spfMerged !== record.value

  return (
    <div className={`dns-row ${cls} ${open ? 'open' : ''}`}>
      <button className="dns-head" onClick={() => setOpen(o => !o)} aria-expanded={open} type="button">
        <span className={`dns-dot ${cls}`} aria-hidden>{glyph}</span>
        <span className="dns-title">{purposeLabel[record.purpose] ?? record.purpose}</span>
        <span className="dns-type">{record.type}</span>
        <span className={`badge ${cls} dns-status`}>{label}</span>
        <span className="mono dns-host-mini">{hostMode === 'label' ? toHostLabel(record.host, domainName) : record.host}</span>
        <span className="dns-caret" aria-hidden>{open ? '▲' : '▼'}</span>
      </button>

      {open && (
        <div className="dns-body">
          <div className="stack">
            <div>
              <div className="small muted" style={{ marginBottom: 4 }}>Host</div>
              <Copyable value={hostMode === 'label' ? toHostLabel(record.host, domainName) : record.host} />
            </div>
            <div>
              <div className="small muted" style={{ marginBottom: 4 }}>Value</div>
              <Copyable value={record.value} />
            </div>
          </div>

          {hasExistingSpf && (
            <div className="spf-merge">
              <strong>You already have an SPF record — don’t add a second one.</strong>
              <p className="small">
                A domain can have only <b>one</b> <span className="mono">v=spf1</span> record; two
                make SPF fail. <b>Delete both</b> your existing SPF and ours, then add this single
                merged record (it keeps your old senders <i>and</i> adds ours):
              </p>
              <Copyable value={spfMerged!} />
            </div>
          )}

          {record.status !== 'ok' && record.status !== 'pending' && (
            <>
              {record.hint && !hasExistingSpf && (
                <div className="alert warn" style={{ marginTop: 12, marginBottom: 0 }}>
                  {hintText[record.hint] ?? record.hint}
                </div>
              )}
              {record.observed && (
                <div className="diff">
                  <div className="box">
                    <div className="k">Expected</div>
                    <div className="v">{record.value}</div>
                  </div>
                  <div className="box">
                    <div className="k">Found in your DNS</div>
                    <div className="v">{record.observed}</div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
